/**
 * DeletedEntityBanner
 *
 * Banner shown at the top of a deleted entity's view.
 * Indicates the entity has been soft-deleted and offers a restore action for admins.
 */

import { useState } from 'react';
import { useThemeColors } from '@/styles';
import { Icon, IconName } from '@/icons';
import { Action } from '@/business/types/action.types';
import { Alert, useAlert } from '@/components/core/Alert';
import { formatDate } from '@/utils/date';

interface DeletedEntityBannerProps {
  /** Type of entity (displayed in the message) */
  entityType: 'board' | 'card' | 'status' | 'template';
  /** When the entity was deleted (ISO timestamp) */
  deletedAt: string;
  /** Restore action to call (e.g., Action.RESTORE_BOARD) */
  restoreAction?: Action;
  /** Input payload for the restore action */
  restoreInput?: Record<string, unknown>;
  /** Whether the current user can restore */
  canRestore?: boolean;
  /** Callback after successful restore */
  onRestored?: () => void;
}

export function DeletedEntityBanner({
  entityType,
  deletedAt,
  restoreAction,
  restoreInput,
  canRestore = false,
  onRestored,
}: DeletedEntityBannerProps) {
  const colors = useThemeColors();
  const alert = useAlert();
  const [isRestoring, setIsRestoring] = useState(false);

  const formattedDate = formatDate(deletedAt);

  const handleRestore = () => {
    if (!restoreAction || !restoreInput) return;

    alert.show({
      type: 'warning',
      title: `Restore ${entityType}?`,
      message: `This will restore the deleted ${entityType} and make it active again.`,
      buttons: [
        { label: 'Cancel', variant: 'outline', onClick: () => {} },
        {
          label: 'Restore',
          variant: 'default',
          onClick: async () => {
            setIsRestoring(true);
            try {
              const { actionOrchestrator } =
                await import('@/business/orchestrator/ActionOrchestrator');
              await actionOrchestrator.execute(restoreAction, restoreInput);
              onRestored?.();
            } catch {
              // Error handled by orchestrator toast
            } finally {
              setIsRestoring(false);
            }
          },
        },
      ],
    });
  };

  return (
    <>
      <Alert {...alert.props} />
      <div
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-3"
        style={{
          backgroundColor: `${colors.error}15`,
          border: `1px solid ${colors.error}40`,
        }}
      >
        <Icon
          name={IconName.TRASH}
          size="md"
          style={{ color: colors.error, flexShrink: 0 }}
        />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium" style={{ color: colors.error }}>
            This {entityType} was deleted on {formattedDate}
          </span>
          <span
            className="text-xs ml-2"
            style={{ color: colors.text.tertiary }}
          >
            (read-only)
          </span>
        </div>
        {canRestore && restoreAction && (
          <button
            onClick={handleRestore}
            disabled={isRestoring}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
            style={{
              border: `1px solid ${colors.error}60`,
              color: colors.error,
              opacity: isRestoring ? 0.6 : 1,
            }}
          >
            {isRestoring ? 'Restoring...' : 'Restore'}
          </button>
        )}
      </div>
    </>
  );
}
